'use client';

import React, { useCallback } from 'react';
import { clsx } from 'clsx';
import { useOpenArticleInShop } from '@paeffgen/react/crm-documents/util';

export interface ArticleTitleButtonProps {
  articleNumber: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Base style for a clickable article title — an inline button that inherits
 * the surrounding text styling and only reveals its affordance on hover/focus.
 */
const ARTICLE_TITLE_BASE_CLASS = clsx(
  'inline text-left bg-transparent border-0 p-0 m-0',
  'font-inherit text-inherit',
  'cursor-pointer transition-colors'
);

/**
 * Shop-link variant: a solid underline on hover/focus — the classic
 * "navigates somewhere" affordance. Used wherever the title opens the shop.
 */
export const ARTICLE_TITLE_BUTTON_CLASS = clsx(
  ARTICLE_TITLE_BASE_CLASS,
  'hover:underline hover:text-primary',
  'focus-visible:outline-none focus-visible:underline focus-visible:text-primary',
  'disabled:cursor-progress disabled:opacity-70 disabled:hover:no-underline disabled:hover:text-inherit'
);

/**
 * Menu-trigger variant: a dotted underline on hover/focus — signals "opens
 * more here" rather than a plain hyperlink, so it stays visually distinct from
 * the shop link. Used by `ArticleNameBlock`'s `onTitleClick`.
 */
export const ARTICLE_TITLE_MENU_CLASS = clsx(
  ARTICLE_TITLE_BASE_CLASS,
  'hover:text-primary hover:underline hover:decoration-dotted hover:underline-offset-2',
  'focus-visible:outline-none focus-visible:text-primary focus-visible:underline focus-visible:decoration-dotted focus-visible:underline-offset-2'
);

export function ArticleTitleButton({
  articleNumber,
  children,
  className,
  onClick,
}: ArticleTitleButtonProps) {
  const { open: openInShop, isLoading } = useOpenArticleInShop();

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onClick?.(e);
      openInShop(articleNumber);
    },
    [articleNumber, openInShop, onClick]
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      aria-busy={isLoading || undefined}
      aria-label="Artikel im Shop öffnen (neuer Tab)"
      title="Im Shop öffnen"
      className={clsx(ARTICLE_TITLE_BUTTON_CLASS, className)}
    >
      {children}
    </button>
  );
}
